import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CodeAcceptanceStatus } from '@brighte/validation';
import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { FormContactFieldsType, VerificationDetailsType } from '../ContactFields';
import { UpdateFields } from './UpdateFields';
import { VerifyFields } from './VerifyFields';

const ERROR_INTERVAL_MS = 5000;

type ContactVerificationProps = {
  verificationDetails?: VerificationDetailsType;
  submitContactDetails: (data: FormContactFieldsType) => void;
  financialProductId?: string;
  isRequesting?: boolean;
  isSubmitting?: boolean;
  isUniversalLogin?: boolean;
} & UseFormReturn<FormContactFieldsType>;

export const ContactVerification = ({
  verificationDetails,
  financialProductId,
  submitContactDetails,
  isRequesting = false,
  isSubmitting = false,
  isUniversalLogin = false,
  ...formContext
}: ContactVerificationProps) => {
  const { register, watch } = formContext;
  const [updateOtpSent, codeAccepted, otpSent] = watch(['updateOtpSent', 'codeAccepted', 'otpSent']);

  const [showTryAgainButton, setShowTryAgainButton] = useState(false);

  const onRefreshButtonClick = () => {
    setShowTryAgainButton(false);
    window.location.reload();
  };

  const onLoadingState = () => {
    setTimeout(() => {
      setShowTryAgainButton(true);
    }, ERROR_INTERVAL_MS);
  };

  const renderVerificationStep = (onLoadingState: () => void) => {
    switch (true) {
      case codeAccepted === CodeAcceptanceStatus.NotAccepted:
      case updateOtpSent === true:
      case otpSent === false && codeAccepted === CodeAcceptanceStatus.NotAccepted:
        return (
          <Box css={css({ maxWidth: 600 })}>
            <VerifyFields
              verificationDetails={verificationDetails}
              financialProductId={financialProductId}
              onRequestCode={submitContactDetails}
              onSubmit={submitContactDetails}
              isRequesting={isRequesting}
              isSubmitting={isSubmitting}
              isUniversalLogin={isUniversalLogin}
              {...formContext}
            />
          </Box>
        );
      case codeAccepted === CodeAcceptanceStatus.Email:
      case codeAccepted === CodeAcceptanceStatus.Sms:
        return (
          <Box css={css({ maxWidth: 600 })}>
            <UpdateFields
              isSubmitting={isSubmitting}
              verificationDetails={verificationDetails}
              onSubmit={submitContactDetails}
              {...formContext}
            />
          </Box>
        );
      default:
        onLoadingState();
        return (
          <Box height="full" width="full" display="flex" alignItems="center" justifyContent="center">
            <Stack gap="large" align="center">
              <Spinner />
              {showTryAgainButton && (
                <Text size="small">
                  There seems to be an issue. <TextLinkButton onClick={onRefreshButtonClick}>Try again?</TextLinkButton>
                </Text>
              )}
            </Stack>
          </Box>
        );
    }
  };

  return (
    <>
      {renderVerificationStep(onLoadingState)}
      <input type="hidden" {...register('verificationType')} />
      <input type="hidden" {...register('otpSent')} />
      <input type="hidden" {...register('codeAccepted')} />
      <input type="hidden" {...register('updateOtpSent')} />
    </>
  );
};
